/**
 * CTA click tracker for Mixpanel.
 * Tracks primary CTAs (Book Now, Learn More, View All, phone, email, etc.).
 * Fires before navigation/lightframe launch. Integrates with FH.autoLightframe callback.
 *
 * Expects window.fhsAnalytics from analytics.js. Elements should have data-cta-* attributes.
 */

const CTA_EVENT = 'click-cta';

/** CTA types enum */
const CTA_TYPES = [
  'booking_lightframe',
  'internal_navigation',
  'external_link',
  'phone',
  'email',
  'form_submit',
];

/**
 * Get component context from element or closest parent with data-fhs-component-* attributes.
 * @param {HTMLElement} el
 * @returns {{ component_type?: string, component_id?: string, pattern_id?: string }}
 */
function getComponentContext(el) {
  if (!el || typeof el.closest !== 'function') return {};
  const componentEl = el.closest('[data-fhs-component-type]');
  if (!componentEl) return {};

  return {
    component_type: componentEl.getAttribute('data-fhs-component-type') || undefined,
    component_id: componentEl.getAttribute('data-fhs-component-id') || undefined,
    pattern_id: componentEl.getAttribute('data-fhs-pattern-id') || undefined,
  };
}

/**
 * Extract CTA properties from an element (data-cta-* attributes and inferred values).
 * @param {HTMLElement} el - Anchor, button, or element with data-cta-* attributes
 * @returns {Object|null} Properties for Mixpanel, or null if not a trackable CTA
 */
function getCtaProperties(el) {
  if (!el || typeof el.getAttribute !== 'function') return null;

  // Prefer explicit data-cta-type; required to track
  const ctaType = el.getAttribute('data-cta-type');
  if (!ctaType || !CTA_TYPES.includes(ctaType)) return null;

  const componentContext = getComponentContext(el);
  const ctaLabel =
    el.getAttribute('data-cta-label') ||
    (el.textContent || '').trim().slice(0, 200);
  const destinationUrl =
    el.getAttribute('data-cta-destination-url') ||
    (el.getAttribute('href') || el.getAttribute('formaction') || '');
  const destinationType =
    el.getAttribute('data-cta-destination-type') || inferDestinationType(destinationUrl, ctaType);
  const buttonLocation = el.getAttribute('data-cta-button-location') || undefined;
  const componentType = el.getAttribute('data-cta-component-type') || componentContext.component_type;
  const componentId = el.getAttribute('data-cta-component-id') || componentContext.component_id;
  const patternId = el.getAttribute('data-cta-pattern-id') || componentContext.pattern_id;

  return {
    component_type: componentType,
    component_id: componentId,
    pattern_id: patternId,
    cta_label: ctaLabel,
    cta_type: ctaType,
    button_location: buttonLocation,
    destination_url: destinationUrl,
    destination_type: destinationType,
  };
}

/**
 * Infer destination_type from URL and cta_type.
 * @param {string} url
 * @param {string} ctaType
 * @returns {string}
 */
function inferDestinationType(url, ctaType) {
  if (ctaType === 'phone' || ctaType === 'email') return ctaType;
  if (ctaType === 'booking_lightframe') return 'activity';
  if (!url || url.startsWith('#') || url.startsWith('javascript:')) return 'unknown';

  try {
    if (url.startsWith('mailto:')) return 'email';
    if (url.startsWith('tel:')) return 'phone';
    if (url.includes('fareharbor.com')) return 'activity';

    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin ? 'page' : 'external';
  } catch {
    return 'unknown';
  }
}

/**
 * Build CTA properties for a booking link when data-cta-* may not be present.
 * Used from FH.autoLightframe callback.
 * @param {HTMLElement} el - The booking link
 * @returns {Object} Properties for Mixpanel
 */
function getBookingCtaProperties(el) {
  const href = el.getAttribute('href') || '';
  const label = (el.textContent || '').trim().slice(0, 200);

  const componentContext = getComponentContext(el);
  const base = {
    component_type: componentContext.component_type,
    component_id: componentContext.component_id,
    pattern_id: componentContext.pattern_id,
    cta_label: el.getAttribute('data-cta-label') || label,
    cta_type: 'booking_lightframe',
    button_location: el.getAttribute('data-cta-button-location'),
    destination_url: href,
    destination_type: 'activity',
  };

  return Object.fromEntries(
    Object.entries(base).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    )
  );
}

/**
 * Track CTA click. Call this before navigation/lightframe.
 * @param {HTMLElement} el - The clicked CTA element
 * @param {string} [forceCtaType] - Override cta_type (e.g. 'booking_lightframe' from FH callback)
 */
export function trackCtaClick(el, forceCtaType) {
  if (typeof window === 'undefined' || !window.fhsAnalytics?.track) return;
  if (!el) return;

  let props;

  if (forceCtaType === 'booking_lightframe') {
    props = getBookingCtaProperties(el);
  } else {
    props = getCtaProperties(el);
    if (!props) return;
  }

  if (Object.keys(props).length === 0) return;
  window.fhsAnalytics.track(CTA_EVENT, props);
}

/**
 * Initialize delegated handlers for CTA elements.
 * Uses capture phase so event fires before navigation/lightframe.
 */
function initDelegatedHandlers() {
  if (typeof document === 'undefined') return;

  // Click handler for links and buttons with data-cta-type
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target.closest('a[data-cta-type], button[data-cta-type]');
      if (!el) return;
      trackCtaClick(el);
    },
    true
  );

  // Submit handler for forms with data-cta-type on submit button
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      const submitBtn =
        form.querySelector('[type="submit"][data-cta-type]') ||
        form.querySelector('button[data-cta-type]');
      if (!submitBtn) return;
      trackCtaClick(submitBtn);
    },
    true
  );
}

// Initialize on load
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDelegatedHandlers);
  } else {
    initDelegatedHandlers();
  }

  window.fhsAnalytics = window.fhsAnalytics || {};
  window.fhsAnalytics.trackCtaClick = trackCtaClick;
}
